import React, { useState } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  CircularProgress,
  Divider,
  InputAdornment,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { fetchJsonDynamic } from "../../../../data/DynamicServiceNew/jsondynamicservice";
import { AccountsandFinanceurl } from "../../../../config";

const SearchCode = () => {
  const [searchCode, setSearchCode] = useState("");
  const [transaction, setTransaction] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const formatDate = (date) => {
    if (!date) return "-";
    const d = new Date(date);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatTransTime = (transTime) => {
    if (!transTime) return "-";
    const str = transTime.toString();
    return `${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)} ${str.slice(8, 10)}:${str.slice(10, 12)}:${str.slice(12, 14)}`;
  };

  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return "0.00";
    return amount.toLocaleString("en-US", { minimumFractionDigits: 2 });
  };

  const handleSearch = async () => {
    if (!searchCode.trim()) return;
    setLoading(true);
    setError(null);
    setTransaction(null);

    try {
      const response = await fetchJsonDynamic(
        "searchIncomingC2BbyCode",
        "searchModel",
        { transId: searchCode.trim() },
        AccountsandFinanceurl.uri,
      );

      if (response?.error) {
        const errorObj = JSON.parse(response.error);
        setError(errorObj.Message || "Transaction not found.");
      } else if (response?.results) {
        const data = JSON.parse(response.results);
        if (data && data.length > 0) {
          setTransaction(data[0]);
        } else {
          setError("No transaction found for the given code.");
        }
      } else {
        setError("No transaction found for the given code.");
      }
    } catch (err) {
      setError("An error occurred while searching. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const StatusChip = ({ value, trueLabel, falseLabel }) => (
    <Chip
      icon={value ? <CheckCircleIcon /> : <CancelIcon />}
      label={value ? trueLabel : falseLabel}
      color={value ? "success" : "default"}
      size="small"
      variant="outlined"
    />
  );

  const InfoRow = ({ label, value }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 0.75,
        borderBottom: "1px solid #f0f0f0",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} sx={{ textAlign: "right" }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} color="primary">
          Transaction Search
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Search for incoming M-Pesa C2B transactions by transaction code
        </Typography>
      </Box>

      <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              label="Transaction Code (TransID)"
              variant="outlined"
              fullWidth
              value={searchCode}
              onChange={(e) => setSearchCode(e.target.value.toUpperCase())}
              onKeyPress={handleKeyPress}
              placeholder="e.g. UEPR55B29Y"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading || !searchCode.trim()}
              sx={{
                height: 56,
                px: 4,
                borderRadius: 1.5,
                whiteSpace: "nowrap",
              }}
            >
              {loading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "Search"
              )}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {transaction && (
        <Card elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box
            sx={{
              bgcolor: "primary.main",
              px: 3,
              py: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Box>
              <Typography variant="h6" color="white" fontWeight={700}>
                {transaction.TransId}
              </Typography>
              <Typography variant="body2" color="rgba(255,255,255,0.8)">
                {transaction.TransactionType}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="h5" color="white" fontWeight={700}>
                KES {formatAmount(transaction.TransAmount)}
              </Typography>
              <Typography variant="body2" color="rgba(255,255,255,0.8)">
                {formatTransTime(transaction.TransTime)}
              </Typography>
            </Box>
          </Box>

          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  color="primary"
                  fontWeight={700}
                  sx={{ mb: 1, letterSpacing: 0.5 }}
                >
                  CUSTOMER DETAILS
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <InfoRow
                  label="Customer Name"
                  value={transaction.CustomerName}
                />
                <InfoRow
                  label="Phone Number"
                  value={transaction.CustomerNumber}
                />
                <InfoRow label="Customer ID" value={transaction.CustomerId} />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  color="primary"
                  fontWeight={700}
                  sx={{ mb: 1, letterSpacing: 0.5 }}
                >
                  BUSINESS DETAILS
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <InfoRow
                  label="Short Code"
                  value={transaction.BusinessShortCode}
                />
                <InfoRow label="Branch" value={transaction.BranchName || "-"} />
                <InfoRow
                  label="Branch ID"
                  value={transaction.BranchId ?? "-"}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  color="primary"
                  fontWeight={700}
                  sx={{ mb: 1, letterSpacing: 0.5 }}
                >
                  STATUS
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                {transaction.IsSuspended === 1 &&
                transaction.IsReconcilled === 1 ? (
                  <Alert
                    severity="warning"
                    icon={<CancelIcon />}
                    sx={{ borderRadius: 1.5 }}
                  >
                    <strong>Suspended C2B</strong> — This transaction was
                    suspended and has since been reconciled.
                  </Alert>
                ) : (
                  <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Reconciled:
                      </Typography>
                      <StatusChip
                        value={transaction.IsReconcilled === 1}
                        trueLabel="Reconciled"
                        falseLabel="Not Reconciled"
                      />
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Suspended:
                      </Typography>
                      <Chip
                        icon={
                          transaction.IsSuspended === 1 ? (
                            <CancelIcon />
                          ) : (
                            <CheckCircleIcon />
                          )
                        }
                        label={
                          transaction.IsSuspended === 1
                            ? "Suspended"
                            : "Active"
                        }
                        color={
                          transaction.IsSuspended === 1 ? "error" : "success"
                        }
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  color="primary"
                  fontWeight={700}
                  sx={{ mb: 1, letterSpacing: 0.5 }}
                >
                  DATES
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <InfoRow
                      label="Posted Date"
                      value={formatDate(transaction.IsPostedDate)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InfoRow
                      label="Created Date"
                      value={formatDate(transaction.CreatedDate)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InfoRow
                      label="Reconciled Date"
                      value={formatDate(transaction.ReconcilledDate)}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default SearchCode;
